import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Hook + component pair for Privacy Policy acknowledgement on public forms.
 *
 * Usage in a form:
 *   const { accepted, setAccepted, version, AcknowledgementCheckbox, validate } = usePrivacyAcceptance();
 *   ...
 *   <AcknowledgementCheckbox testid="contact-privacy-accept" />
 *   <button disabled={!accepted || submitting} onClick={onSubmit}>...</button>
 *
 * In onSubmit:
 *   if (!validate()) { setError("Please accept the Privacy Policy."); return; }
 *   await api.post("/...", { ...form, privacy_accepted: accepted, privacy_policy_version: version });
 */
export function usePrivacyAcceptance() {
  const [accepted, setAccepted] = useState(false);
  const [version, setVersion] = useState("");

  useEffect(() => {
    let active = true;
    api.get("/privacy-policy")
      .then((r) => { if (active && r.data?.version) setVersion(r.data.version); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const validate = useCallback(() => accepted, [accepted]);

  const AcknowledgementCheckbox = useCallback(
    ({ testid = "privacy-acknowledgement", className = "" }) => (
      <label
        className={`flex items-start gap-3 text-sm text-foreground select-none ${className}`}
        data-testid={`${testid}-label`}
      >
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-1 h-4 w-4 accent-execution cursor-pointer"
          data-testid={testid}
          aria-required="true"
          required
        />
        <span>
          I have read and agree to the{" "}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-execution underline hover:text-[#00C779]"
            data-testid={`${testid}-link`}
          >
            Privacy Policy
          </Link>
          .
        </span>
      </label>
    ),
    [accepted]
  );

  return { accepted, setAccepted, version, AcknowledgementCheckbox, validate };
}
